import React, { Dispatch, SetStateAction } from "react";

import moment from "moment";

import { getCurrentProgramTermsAndConditionsByProgramId } from "services";

import { formatDate, openFileInNewTab, queryIsLoading, useGetCarbonProgramById, useGetProgramContractByProgramId, useTranslation } from "utils";

import { PROGRAM_TYPE, ProgramContractDetails, USER_TYPE, userType } from "models";

import { Checkbox, Flex, GuidanceLink, Spin, Tag, Typography } from "components";

type ProgramTermsProps = {
  contractDetails: ProgramContractDetails;
  userType: userType;
  setCheckboxState: Dispatch<SetStateAction<ProgramContractDetails[]>>;
};

export const ProgramTerms = ({ contractDetails, userType, setCheckboxState }: ProgramTermsProps) => {
  const { t } = useTranslation("dashboard");
  const programQuery = useGetCarbonProgramById(contractDetails.programId);
  const { data: programContract } = useGetProgramContractByProgramId({ programId: contractDetails.programId });

  const programType = programQuery.data?.partnerId ? PROGRAM_TYPE.INSETTING : PROGRAM_TYPE.OFFSETTING;

  const handleProgramTermsClick = async () => {
    const data = await getCurrentProgramTermsAndConditionsByProgramId(contractDetails.programId);
    openFileInNewTab(data);
  };

  const handleCheckboxClick = () => {
    setCheckboxState((prev) =>
      prev.reduce(
        (acc, curr) => (curr.programId === contractDetails.programId ? [...acc, { ...curr, accepted: !curr.accepted }] : [...acc, curr]),
        [] as ProgramContractDetails[]
      )
    );
  };

  if ([programQuery].some(queryIsLoading)) {
    return <Spin size="small" />;
  }

  return (
    <Flex className="margin-top-250" vertical gap="0.5rem">
      <Flex align="center" gap="0.25rem" wrap="wrap">
        {userType === USER_TYPE.EXISTING && <Checkbox checked={contractDetails.accepted} onClick={handleCheckboxClick} />}
        <Typography variant="h3" className="margin-right-50">
          {programQuery.data?.programName}
        </Typography>
        {contractDetails.farmNames.map((name, index) => (
          <Tag key={index} text={name} colour="yellow" />
        ))}
      </Flex>
      {userType === USER_TYPE.EXISTING && programContract?.createdAt && (
        <Typography variant="paragraph-m">
          {t("termsOverlay.deadline", {
            createdAt: formatDate(programContract?.createdAt, "LL", moment.ISO_8601),
            signBy: formatDate(
              moment(programContract?.createdAt)
                .add(30, "days")
                .toISOString(),
              "LL",
              moment.ISO_8601
            ),
          })}
        </Typography>
      )}
      <Typography variant="paragraph-m" style={{ whiteSpace: "pre-line" }}>
        {userType === USER_TYPE.NEW ? t(`termsOverlay.newUser.programTermsDescription.${programType}`) : programContract?.description}
      </Typography>
      <Flex align="center">
        <Typography variant="paragraph-m">{t("termsOverlay.linkDescription")}</Typography>
        <GuidanceLink text={t("termsOverlay.programTermsLink")} onClick={handleProgramTermsClick} />
      </Flex>
    </Flex>
  );
};

export default ProgramTerms;
